import { useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

/**
 * Reusable full-screen image lightbox.
 *
 * Props:
 *  - items:  Array<{ src: string, caption?: string, title?: string }>
 *  - index:  number  (>=0 opens the lightbox at that index; <0 keeps it closed)
 *  - onClose: () => void
 *  - onChange: (nextIndex: number) => void
 *  - testIdPrefix: string  (default "lightbox") — used to namespace data-testid values
 */
export default function Lightbox({ items, index, onClose, onChange, testIdPrefix = "lightbox" }) {
  const open = Array.isArray(items) && index >= 0 && index < items.length;
  const total = open ? items.length : 0;

  const goPrev = useCallback(() => {
    if (!open) return;
    onChange((index - 1 + total) % total);
  }, [open, index, total, onChange]);

  const goNext = useCallback(() => {
    if (!open) return;
    onChange((index + 1) % total);
  }, [open, index, total, onChange]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, goPrev, goNext]);

  const current = open ? items[index] : null;

  return (
    <AnimatePresence>
      {open && current && (
        <motion.div
          key="lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[80] bg-forest/95 backdrop-blur-sm flex items-center justify-center"
          onClick={onClose}
          data-testid={testIdPrefix}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="absolute top-5 right-5 z-10 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-parchment transition-colors"
            aria-label="Tutup"
            data-testid={`${testIdPrefix}-close`}
          >
            <X size={22} />
          </button>

          <div
            className="absolute top-5 left-5 z-10 text-[11px] tracking-[0.35em] uppercase text-parchment/70"
            data-testid={`${testIdPrefix}-counter`}
          >
            {index + 1} / {total}
          </div>

          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-3 md:left-6 z-10 inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 text-parchment transition-colors disabled:opacity-40"
            aria-label="Gambar sebelumnya"
            data-testid={`${testIdPrefix}-prev`}
            disabled={total <= 1}
          >
            <ChevronLeft size={26} />
          </button>

          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-3 md:right-6 z-10 inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 text-parchment transition-colors disabled:opacity-40"
            aria-label="Gambar berikutnya"
            data-testid={`${testIdPrefix}-next`}
            disabled={total <= 1}
          >
            <ChevronRight size={26} />
          </button>

          <div
            className="relative max-w-[92vw] max-h-[86vh] flex flex-col items-center gap-4 px-4 md:px-16"
            onClick={(e) => e.stopPropagation()}
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={index}
                src={current.src}
                alt={current.title || current.caption || `Gambar ${index + 1}`}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="max-w-full max-h-[74vh] object-contain rounded-lg shadow-2xl"
                data-testid={`${testIdPrefix}-image`}
              />
            </AnimatePresence>
            {(current.title || current.caption) && (
              <motion.div
                key={`cap-${index}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, delay: 0.05 }}
                className="max-w-3xl text-center"
                data-testid={`${testIdPrefix}-caption`}
              >
                {current.title && (
                  <p className="font-serif-display text-parchment text-xl md:text-2xl leading-snug">
                    {current.title}
                  </p>
                )}
                {current.caption && (
                  <p className="mt-2 text-parchment/80 text-sm md:text-base leading-relaxed">
                    {current.caption}
                  </p>
                )}
              </motion.div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
